import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, ExternalLink, Plus, Edit, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { ProjectForm } from './ProjectForm';
import { EditableProjectForm } from './EditableProjectForm';
import { MediaDisplay } from '../MediaDisplay';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Sortable Action Component
const SortableAction = ({ action, isAdmin, onEdit, onDelete, editingActionId, onEditSubmit, actionLoading }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: action.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditing = editingActionId === action.id;

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="mb-6 relative">
        {isAdmin && !isEditing && (
          <div
            {...attributes}
            {...listeners}
            className="absolute top-4 right-4 z-10 p-2 bg-white rounded-md shadow-md cursor-grab active:cursor-grabbing hover:bg-slate-100"
          >
            <GripVertical className="h-5 w-5 text-slate-500" />
          </div>
        )}
        <CardHeader>
          <div className="flex justify-between items-start">
            <CardTitle>
              <div dangerouslySetInnerHTML={{ __html: action.title }} />
            </CardTitle>
            {isAdmin && !isEditing && (
              <div className="flex gap-2 ml-12">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(action.id)}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Editar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(action.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  Eliminar
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <EditableProjectForm
              initialData={action}
              onSubmit={(data) => onEditSubmit(action.id, data)}
              submitText="Guardar Acción"
              loading={actionLoading}
              onCancel={() => onEdit(null)}
            />
          ) : (
            <>
              <div 
                className="text-slate-700 mb-4 prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: action.description }}
              />

              {action.images && action.images.length > 0 && (
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">Imágenes/Videos:</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {action.images.map((img, idx) => (
                      <MediaDisplay
                        key={idx}
                        url={img.url}
                        alt={img.description || `Media ${idx + 1}`}
                        description={img.description}
                        external_link={img.external_link}
                        external_link_text={img.external_link_text}
                        className="w-full rounded-lg shadow-md"
                        allMedia={action.images}
                        currentIndex={idx}
                      />
                    ))}
                  </div>
                </div>
              )}

              {action.links && action.links.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Enlaces:</h4>
                  <div className="space-y-2">
                    {action.links.map((link, idx) => (
                      <a
                        key={idx}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                      >
                        <ExternalLink className="h-4 w-4" />
                        {link.title}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActionForm, setShowActionForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingActionId, setEditingActionId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const { user, token } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = user?.role === 'admin';

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadProject();
  }, [id]);

  const loadProject = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/zona-pro/projects/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setProject(response.data);
    } catch (error) {
      toast.error('Error cargando proyecto');
      navigate('/zona-pro/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id && project.actions) {
      const oldIndex = project.actions.findIndex((a) => a.id === active.id);
      const newIndex = project.actions.findIndex((a) => a.id === over.id);

      const newActions = arrayMove(project.actions, oldIndex, newIndex);
      setProject({ ...project, actions: newActions });

      // Save new order to backend
      try {
        await axios.put(
          `${API_URL}/api/zona-pro/projects/${id}/actions/reorder`,
          {
            items: newActions.map((a) => a.id),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success('Orden actualizado');
      } catch (error) {
        toast.error('Error actualizando el orden');
        // Revert on error
        loadProject();
      }
    }
  };

  const handleAddAction = async (actionData) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/zona-pro/projects/${id}/actions`, actionData);
      toast.success('Acción añadida correctamente');
      setShowActionForm(false);
      loadProject();
    } catch (error) {
      toast.error('Error añadiendo acción');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAction = async (actionId) => {
    if (!confirm('¿Seguro que quieres eliminar esta acción?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/zona-pro/projects/${id}/actions/${actionId}`);
      toast.success('Acción eliminada');
      loadProject();
    } catch (error) {
      toast.error('Error eliminando acción');
    }
  };

  const handleEditProject = async (projectData) => {
    setEditLoading(true);
    try {
      await axios.put(`${API_URL}/api/zona-pro/projects/${id}`, projectData);
      toast.success('Proyecto actualizado correctamente');
      setShowEditForm(false);
      loadProject();
    } catch (error) {
      toast.error('Error actualizando proyecto');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEditAction = async (actionId, actionData) => {
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/zona-pro/projects/${id}/actions/${actionId}`, actionData);
      toast.success('Acción actualizada correctamente');
      setEditingActionId(null);
      loadProject();
    } catch (error) {
      toast.error('Error actualizando acción');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-slate-500">Cargando proyecto...</p>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-6 py-4">
          <Button
            onClick={() => navigate('/zona-pro/dashboard')}
            variant="ghost"
            size="sm"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a Proyectos
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="flex justify-between items-start mb-8">
          <h1 className="text-4xl font-serif font-bold text-slate-800">
            <div dangerouslySetInnerHTML={{ __html: project.title }} />
          </h1>
          {isAdmin && (
            <Button
              onClick={() => setShowEditForm(!showEditForm)}
              variant="outline"
            >
              <Edit className="mr-2 h-4 w-4" />
              {showEditForm ? 'Cancelar' : 'Editar Proyecto'}
            </Button>
          )}
        </div>

        {/* Edit Project Form */}
        {showEditForm && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Editar Proyecto</CardTitle>
            </CardHeader>
            <CardContent>
              <EditableProjectForm
                initialData={project}
                onSubmit={handleEditProject}
                onCancel={() => setShowEditForm(false)}
                submitText="Guardar Cambios"
                loading={editLoading}
              />
            </CardContent>
          </Card>
        )}

        {/* Images/Videos with descriptions */}
        {project.images && project.images.length > 0 && (
          <div className="mb-8 space-y-6">
            {project.images.map((image, index) => (
              <MediaDisplay
                key={index}
                url={image.url}
                alt={image.description || `${project.title} - ${index + 1}`}
                description={image.description}
                external_link={image.external_link}
                external_link_text={image.external_link_text}
                className="w-full rounded-lg shadow-lg"
                allMedia={project.images}
                currentIndex={index}
              />
            ))}
          </div>
        )}

        {/* Description */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Descripción</CardTitle>
          </CardHeader>
          <CardContent>
            <div 
              className="prose prose-sm max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: project.description }}
            />
          </CardContent>
        </Card>

        {/* Links */}
        {project.links && project.links.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Enlaces Relacionados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {project.links.map((link, index) => (
                  <a
                    key={index}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-3 rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    <ExternalLink className="h-4 w-4 text-emerald-600" />
                    <span className="text-slate-700">{link.title}</span>
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions/Phases */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-serif font-bold text-slate-800">
              Acciones / Fases del Proyecto
            </h2>
            {isAdmin && (
              <Button
                onClick={() => setShowActionForm(!showActionForm)}
                variant="outline"
              >
                <Plus className="mr-2 h-4 w-4" />
                {showActionForm ? 'Cancelar' : 'Añadir Acción'}
              </Button>
            )}
          </div>

          {/* Add Action Form */}
          {showActionForm && (
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Nueva Acción/Fase</CardTitle>
              </CardHeader>
              <CardContent>
                <ProjectForm
                  onSubmit={handleAddAction}
                  submitText="Añadir Acción"
                  loading={actionLoading}
                />
              </CardContent>
            </Card>
          )}

          {/* Actions List */}
          {project.actions && project.actions.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={project.actions.map((a) => a.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-4">
                  {isAdmin && (
                    <p className="text-sm text-slate-600 mb-4">
                      <span className="text-emerald-600 font-medium">💡 Arrastra las acciones para reordenarlas</span>
                    </p>
                  )}
                  {project.actions.map((action) => (
                    <SortableAction
                      key={action.id}
                      action={action}
                      isAdmin={isAdmin}
                      onEdit={setEditingActionId}
                      onDelete={handleDeleteAction}
                      editingActionId={editingActionId}
                      onEditSubmit={handleEditAction}
                      actionLoading={actionLoading}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-slate-500">
                  No hay acciones o fases añadidas aún.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};